import React, { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { FileSpreadsheet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { USCGResourceKindOptions } from 'components/IncidentConfig/ResourceTypes';
import { AssetImportRowsTable } from 'components/DisasterGPT/agentConfirm/components/AssetImportRowsTable';
import {
  countPlanned,
  defaultSelection,
} from 'components/DisasterGPT/agentConfirm/assetImportReviewModel';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  downloadAssetImportTemplate,
  useCommitAssetImport,
  useParseAssetFile,
  usePreviewAssetImport,
  type AssetImportPreview,
} from 'hooks/assetImports/assetImportHooks';
import {
  ASSET_IMPORT_FIELDS,
  ASSET_IMPORT_SYMBOLOGY_KEYS,
  defaultSheet,
  initialDefaults,
  initialMapping,
  mappingProblem,
  type AssetColumnMapping,
  type AssetImportDefaultsInput,
  type AssetImportField,
  type GeocodeMode,
  type ParsedImportFile,
  type ParsedImportSheet,
} from 'hooks/assetImports/assetImportDialogModel';
import { USCG_SYMBOLOGY_OPTIONS } from '../LayersTab/utils/uscgSymbology';

type Step = 'upload' | 'map' | 'defaults' | 'review';

const STEP_TITLES: Record<Step, string> = {
  upload: 'Choose a spreadsheet',
  map: 'Match columns to asset fields',
  defaults: 'Settings for every asset',
  review: 'Review and import',
};

const NOT_USED = '__not_used__';
const INFER = '__infer__';
const ACCEPT = '.xls,.xlsx,.csv';

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  if (isAxiosError(err)) {
    const message = (err.response?.data as { message?: string } | undefined)
      ?.message;
    if (message) return message;
  }
  return 'Something went wrong. Try again.';
}

const SYMBOLOGY_OPTIONS = USCG_SYMBOLOGY_OPTIONS.filter((option) =>
  ASSET_IMPORT_SYMBOLOGY_KEYS.includes(option.type)
);

export function AssetImportDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the batch id after the import is saved. */
  onImported: (batchId: string) => void;
}) {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedImportFile | null>(null);
  const [sheetName, setSheetName] = useState('');
  const [mapping, setMapping] = useState<AssetColumnMapping>({});
  const [defaults, setDefaults] = useState<AssetImportDefaultsInput>({});
  const [geocodeMode, setGeocodeMode] = useState<GeocodeMode>('auto');
  const [saveMapping, setSaveMapping] = useState(true);
  const [preview, setPreview] = useState<AssetImportPreview | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [updateExisting, setUpdateExisting] = useState(true);

  const parse = useParseAssetFile();
  const previewImport = usePreviewAssetImport();
  const commit = useCommitAssetImport();
  const { data: rules = [] } = useOrgEventAutomationRules();

  const sheet = parsed?.sheets.find((s) => s.name === sheetName);
  const problem = mappingProblem(mapping);
  const tooManyRows = !!sheet && !!parsed && sheet.rowCount > parsed.maxRows;
  const planned = preview
    ? countPlanned(preview.rows, selected, updateExisting)
    : null;
  const importCount = planned ? planned.create + planned.update : 0;

  const ruleOptions = useMemo(
    () =>
      rules
        .filter((rule) => !rule.archived)
        .map((rule) => ({ value: rule.id, label: rule.name })),
    [rules]
  );

  const reset = () => {
    setStep('upload');
    setFile(null);
    setParsed(null);
    setSheetName('');
    setMapping({});
    setDefaults({});
    setGeocodeMode('auto');
    setSaveMapping(true);
    setPreview(null);
    setSelected([]);
    setUpdateExisting(true);
    parse.reset();
    previewImport.reset();
    commit.reset();
  };

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const applySheet = (next: ParsedImportSheet) => {
    setSheetName(next.name);
    setMapping(initialMapping(next));
    const restored = initialDefaults(next);
    setDefaults(restored.defaults);
    setGeocodeMode(restored.geocodeMode);
  };

  const chooseFile = (next: File | undefined) => {
    if (!next) return;
    setFile(next);
    setParsed(null);
    parse.mutate(next, {
      onSuccess: (result) => {
        setParsed(result);
        const first = defaultSheet(result);
        if (first) applySheet(first);
      },
    });
  };

  const runPreview = () => {
    if (!file || !sheet) return;
    previewImport.mutate(
      { file, sheet: sheet.name, mapping, defaults, geocodeMode },
      {
        onSuccess: (result) => {
          setPreview(result);
          setSelected(defaultSelection(result.rows));
          setUpdateExisting(true);
          setStep('review');
        },
      }
    );
  };

  const runImport = () => {
    if (!preview) return;
    const selectable = new Set(
      preview.rows.filter((r) => r.selectable).map((r) => r.index)
    );
    commit.mutate(
      {
        batchId: preview.batchId,
        include_rows: selected.filter((n) => selectable.has(n)),
        update_existing: updateExisting,
        saveMapping,
      },
      {
        onSuccess: () => {
          onImported(preview.batchId);
          close(false);
        },
      }
    );
  };

  const setField = (field: AssetImportField, header: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (header === NOT_USED) delete next[field];
      else next[field] = header;
      return next;
    });
  };

  const mappedFields = ASSET_IMPORT_FIELDS.filter((f) => mapping[f.field]);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="dark tw:max-h-[90vh] tw:!max-w-3xl tw:overflow-y-auto tw:bg-background tw:text-foreground"
        data-testid="asset-import-dialog"
      >
        <DialogHeader>
          <DialogTitle>Import assets</DialogTitle>
          <DialogDescription>{STEP_TITLES[step]}</DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="tw:space-y-3">
            <label
              className="tw:flex tw:cursor-pointer tw:flex-col tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-dashed tw:border-border tw:px-4 tw:py-8 tw:text-center tw:text-sm tw:hover:border-ring"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                chooseFile(event.dataTransfer.files?.[0]);
              }}
            >
              <FileSpreadsheet className="tw:size-6 tw:text-muted-foreground" />
              <span>
                {file
                  ? file.name
                  : 'Drop an .xlsx, .xls or .csv file here, or choose one'}
              </span>
              <span className="tw:text-[12px] tw:text-muted-foreground">
                One asset per row, up to 1,000 rows and 10 MB.
              </span>
              <input
                type="file"
                accept={ACCEPT}
                className="tw:sr-only"
                aria-label="Spreadsheet file"
                onChange={(event) => chooseFile(event.target.files?.[0])}
              />
            </label>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="tw:px-0"
              onClick={() => void downloadAssetImportTemplate()}
            >
              Download template
            </Button>
            {parse.isPending && (
              <p className="tw:text-sm tw:text-muted-foreground">
                Reading the file…
              </p>
            )}
            <ErrorText message={errorMessage(parse.error)} />
            {parsed && parsed.sheets.length > 1 && (
              <Field>
                <FieldLabel htmlFor="asset-import-sheet">Sheet</FieldLabel>
                <Select
                  value={sheetName}
                  onValueChange={(next) => {
                    const match = parsed.sheets.find(
                      (s) => s.name === String(next)
                    );
                    if (match) applySheet(match);
                  }}
                >
                  <SelectTrigger
                    id="asset-import-sheet"
                    size="sm"
                    className="tw:w-full"
                  >
                    <SelectValue>{sheetName}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="dark">
                    {parsed.sheets.map((s) => (
                      <SelectItem key={s.name} value={s.name}>
                        {s.name} ({s.rowCount} rows)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {sheet && (
              <p className="tw:text-sm tw:text-muted-foreground">
                {sheet.rowCount} row{sheet.rowCount === 1 ? '' : 's'} found
                {sheet.savedMapping
                  ? '. Column matches from your last import of this layout were restored.'
                  : '.'}
              </p>
            )}
            {tooManyRows && (
              <ErrorText
                message={`This sheet has ${sheet!.rowCount} rows; the limit is ${parsed!.maxRows}. Split the file and import each part.`}
              />
            )}
          </div>
        )}

        {step === 'map' && sheet && (
          <div className="tw:space-y-4">
            <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
              {ASSET_IMPORT_FIELDS.map((f) => (
                <Field key={f.field}>
                  <FieldLabel htmlFor={`asset-import-field-${f.field}`}>
                    {f.label}
                    {'required' in f && f.required ? ' *' : ''}
                  </FieldLabel>
                  <Select
                    value={mapping[f.field] ?? NOT_USED}
                    onValueChange={(next) => setField(f.field, String(next))}
                  >
                    <SelectTrigger
                      id={`asset-import-field-${f.field}`}
                      size="sm"
                      className="tw:w-full"
                    >
                      <SelectValue>
                        {mapping[f.field] ?? 'Not used'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="dark">
                      <SelectItem value={NOT_USED}>Not used</SelectItem>
                      {sheet.headers.map((header) => (
                        <SelectItem key={header} value={header}>
                          {header}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ))}
            </div>
            {mappedFields.length > 0 && sheet.sample.length > 0 && (
              <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
                <table className="tw:w-full tw:border-collapse tw:text-[12px]">
                  <thead className="tw:bg-muted">
                    <tr className="tw:text-left tw:text-muted-foreground">
                      {mappedFields.map((f) => (
                        <th
                          key={f.field}
                          className="tw:px-2 tw:py-1.5 tw:font-medium"
                        >
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.sample.map((row, i) => (
                      <tr key={i} className="tw:border-t tw:border-border">
                        {mappedFields.map((f) => (
                          <td key={f.field} className="tw:px-2 tw:py-1.5">
                            {String(row[mapping[f.field]!] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ErrorText message={problem} muted />
          </div>
        )}

        {step === 'defaults' && (
          <div className="tw:space-y-4">
            <Field>
              <FieldLabel htmlFor="asset-import-kind">Kind</FieldLabel>
              <Select
                value={defaults.kind || 'Facilities'}
                onValueChange={(next) =>
                  setDefaults((prev) => ({ ...prev, kind: String(next) }))
                }
              >
                <SelectTrigger
                  id="asset-import-kind"
                  size="sm"
                  className="tw:w-full"
                >
                  <SelectValue>{defaults.kind || 'Facilities'}</SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  {USCGResourceKindOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="asset-import-symbol">Map symbol</FieldLabel>
              <Select
                value={defaults.symbology ?? INFER}
                onValueChange={(next) =>
                  setDefaults((prev) => ({
                    ...prev,
                    symbology:
                      String(next) === INFER ? undefined : String(next),
                  }))
                }
              >
                <SelectTrigger
                  id="asset-import-symbol"
                  size="sm"
                  className="tw:w-full"
                >
                  <SelectValue>
                    {SYMBOLOGY_OPTIONS.find(
                      (o) => o.type === defaults.symbology
                    )?.label ?? 'Infer from each row’s type and name'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  <SelectItem value={INFER}>
                    Infer from each row’s type and name
                  </SelectItem>
                  {SYMBOLOGY_OPTIONS.map((option) => (
                    <SelectItem key={option.type} value={option.type}>
                      <span className="tw:inline-flex tw:items-center tw:gap-2">
                        {option.iconSrc ? (
                          <img
                            src={option.iconSrc}
                            alt=""
                            className="symbology-icon"
                          />
                        ) : null}
                        {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Automation rules</FieldLabel>
              <MultiIdCombobox
                options={ruleOptions}
                value={defaults.automation_rule_ids ?? []}
                onChange={(next) =>
                  setDefaults((prev) => ({
                    ...prev,
                    automation_rule_ids: next,
                  }))
                }
                placeholder="Select automation rules"
              />
              <FieldDescription>
                Every imported asset is watched by these rules and tagged with
                their threat types.
              </FieldDescription>
            </Field>
            <label className="tw:flex tw:items-start tw:gap-2 tw:text-sm">
              <Switch
                checked={geocodeMode === 'auto'}
                onCheckedChange={(checked: boolean) =>
                  setGeocodeMode(checked ? 'auto' : 'coordinates_only')
                }
                aria-label="Look up addresses"
              />
              <span>
                Look up addresses
                <span className="tw:block tw:text-[12px] tw:text-muted-foreground">
                  Rows without latitude and longitude are placed from their
                  address. Turn off to use only the coordinates in the file.
                </span>
              </span>
            </label>
            <label className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
              <Checkbox
                checked={saveMapping}
                onCheckedChange={(checked: boolean) => setSaveMapping(checked)}
                aria-label="Remember this mapping"
              />
              Remember these choices for files with the same columns
            </label>
            {previewImport.isPending && (
              <p className="tw:text-sm tw:text-muted-foreground">
                Checking every row
                {geocodeMode === 'auto' ? ' and looking up addresses' : ''}…
              </p>
            )}
            <ErrorText message={errorMessage(previewImport.error)} />
          </div>
        )}

        {step === 'review' && preview && (
          <div className="tw:space-y-2">
            <AssetImportRowsTable
              rows={preview.rows}
              selected={selected}
              updateExisting={updateExisting}
              onSelectedChange={setSelected}
              onUpdateExistingChange={setUpdateExisting}
            />
            <ErrorText message={errorMessage(commit.error)} />
          </div>
        )}

        <DialogFooter className="tw:gap-2">
          {step !== 'upload' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={previewImport.isPending || commit.isPending}
              onClick={() =>
                setStep(
                  step === 'map'
                    ? 'upload'
                    : step === 'defaults'
                      ? 'map'
                      : 'defaults'
                )
              }
            >
              Back
            </Button>
          )}
          {step === 'upload' && (
            <Button
              type="button"
              size="sm"
              disabled={!sheet || sheet.rowCount === 0 || tooManyRows}
              onClick={() => setStep('map')}
            >
              Next
            </Button>
          )}
          {step === 'map' && (
            <Button
              type="button"
              size="sm"
              disabled={!!problem}
              onClick={() => setStep('defaults')}
            >
              Next
            </Button>
          )}
          {step === 'defaults' && (
            <Button
              type="button"
              size="sm"
              disabled={previewImport.isPending}
              onClick={runPreview}
            >
              Preview import
            </Button>
          )}
          {step === 'review' && (
            <Button
              type="button"
              size="sm"
              disabled={importCount === 0 || commit.isPending}
              onClick={runImport}
              data-testid="asset-import-submit"
            >
              {commit.isPending
                ? 'Importing…'
                : `Import ${importCount} asset${importCount === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ErrorText({
  message,
  muted,
}: {
  message: string | null;
  muted?: boolean;
}) {
  if (!message) return null;
  return (
    <p
      role={muted ? undefined : 'alert'}
      className={`tw:text-sm ${muted ? 'tw:text-muted-foreground' : 'tw:text-destructive'}`}
    >
      {message}
    </p>
  );
}
